import { useQueryClient } from '@tanstack/react-query';
import { useIntl } from '@mfw/components';

import type { UiUserUpdateArgs } from '../../api/types/user.types';

import messages from '../../pages/Profiles/AccountPage/messages';
import { useAuthContext, useSnackbar } from '../../context';
import { applyProfileUpdate } from './applyProfileUpdate';
import { updateUser } from '../../api/users.api';
import { queryKeys } from '../../queryKeys';
import { useMutation } from '../base';

export function useUpdateUser() {
  const { setCurrentUser } = useAuthContext();
  const { formatMessage } = useIntl();
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (args: UiUserUpdateArgs) => {
      const response = await updateUser(args);
      if (response) {
        // PATCH /auth/me responds without organizationUsers/roles, so a whole
        // response spread would wipe membership state — merge selectively.
        setCurrentUser((prev) => applyProfileUpdate(prev, response));
      }
    },
    onSuccess: async () => {
      queryClient.invalidateQueries({
        exact: false,
        queryKey: queryKeys.organizations.fetchOrganizationById._def,
      });
      // A personal trainer IS their organization: this same PATCH renames the
      // TRAINER org server-side. The sidebar switcher reads that name (and the
      // org list its own branding snapshot comes from) elsewhere, so without
      // these two the rename showed up only after a hard refresh.
      queryClient.invalidateQueries({
        exact: false,
        queryKey: queryKeys.users.fetchLogicalProfiles().queryKey,
      });
      queryClient.invalidateQueries({
        exact: false,
        queryKey:
          queryKeys.organizations.fetchInfiniteSwitcherOrganizations._def,
      });
      // 7.3 — sex and birth date decide the fitness score rows and the server
      // recomputes the score when they change; the panel and the dashboard
      // KPI must read that score, not their cached one
      queryClient.invalidateQueries({
        exact: false,
        queryKey: queryKeys.statsFitnessScore.fetchFitnessScore._def,
      });
      queryClient.invalidateQueries({
        exact: false,
        queryKey: queryKeys.statsKpi._def,
      });
      enqueueSnackbar({
        message: formatMessage(messages.updateSuccess),
        variant: 'success',
      });
    },
  });
}
